import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, UserCheck, Calendar, Mail, FileText, UserPlus, ShieldCheck, Eye, UserX, MoreHorizontal, UserCog, ChevronLeft, ChevronRight, Building2, EyeOff, X, AlertTriangle } from 'lucide-react';
import { AddPatientDialog } from '@/components/admin/AddPatientDialog';
import { AddSupervisorDialog } from '@/components/admin/AddSupervisorDialog';
import { PatientDeactivateDialog } from '@/components/admin/PatientDeactivateDialog';
import { BulkPatientAssignmentDialog } from '@/components/admin/BulkPatientAssignmentDialog';
import { BulkPatientUnassignDialog } from '@/components/admin/BulkPatientUnassignDialog';
import { QuickAssignDialog } from '@/components/admin/QuickAssignDialog';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useHIPAAMasking } from '@/hooks/useHIPAAMasking';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  dob: string | null;
  created_at: string;
  risk_flags: any;
  agency_id: string;
  user_id: string | null;
  is_active: boolean;
  agency_name?: string;
  hasRedFlags?: boolean;
  _count?: {
    sessions: number;
  };
  assigned_supervisors?: { user_id: string; email: string }[];
}

interface Agency {
  id: string;
  name: string;
}

const PAGE_SIZE = 100;

export default function AdminPatientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAdminAuth();
  const { openImpersonationView, canImpersonate } = useImpersonation();
  const { getDisplayData, isSupervisor, shouldMaskPatient } = useHIPAAMasking();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const initialStatusFilter = searchParams.get('status') as 'all' | 'active' | 'inactive' | 'red_flags' || 'active';
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'red_flags'>(initialStatusFilter);
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [agencyFilter, setAgencyFilter] = useState<string>('');
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [showAddSupervisor, setShowAddSupervisor] = useState(false);
  const [patientToDeactivate, setPatientToDeactivate] = useState<Patient | null>(null);
  const [patientToQuickAssign, setPatientToQuickAssign] = useState<Patient | null>(null);
  const [showBulkAssignment, setShowBulkAssignment] = useState(false);
  const [showBulkUnassign, setShowBulkUnassign] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<Set<string>>(new Set());
  const [pendingUnassign, setPendingUnassign] = useState<{
    patientId: string;
    patientName: string;
    supervisorUserId: string;
    supervisorEmail: string;
  } | null>(null);
  
  // Supervisor filter from URL
  const supervisorFilter = searchParams.get('supervisor') || '';
  const [supervisorEmail, setSupervisorEmail] = useState<string>('');
  const [assignedPatientIds, setAssignedPatientIds] = useState<string[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    activeCount: 0,
    inactiveCount: 0,
    newThisWeek: 0,
    totalSessions: 0,
    unassignedCount: 0
  });

  const isSuperAdmin = user?.role === 'super_admin';
  const canManageAssignments = user?.role === 'super_admin' || user?.role === 'agency_admin';

  // Load agencies for filter (super admin only)
  useEffect(() => {
    if (isSuperAdmin) {
      loadAgencies();
    }
  }, [isSuperAdmin]);

  // Load supervisor's assigned patients when filter is present
  useEffect(() => {
    if (supervisorFilter) {
      loadSupervisorAssignments();
    } else {
      setAssignedPatientIds([]);
      setSupervisorEmail('');
    }
  }, [supervisorFilter]);

  useEffect(() => {
    if (user) {
      // Reset to page 1 when filters change
      setCurrentPage(1);
    }
  }, [searchTerm, statusFilter, agencyFilter, supervisorFilter, showUnassignedOnly, user]);

  useEffect(() => {
    if (user) {
      loadPatients();
      loadStats();
    }
  }, [user, currentPage, statusFilter, agencyFilter, supervisorFilter, assignedPatientIds, showUnassignedOnly]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (user) {
        setCurrentPage(1);
        loadPatients();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadSupervisorAssignments = async () => {
    if (!supervisorFilter) return;

    try {
      // Get supervisor email
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .eq('user_id', supervisorFilter)
        .maybeSingle();

      if (profile) {
        setSupervisorEmail(profile.email);
      }

      // Get assigned patient IDs
      const { data: assignments } = await supabase
        .from('patient_assignments')
        .select('patient_id')
        .eq('staff_user_id', supervisorFilter)
        .is('revoked_at', null);

      setAssignedPatientIds((assignments || []).map(a => a.patient_id));
    } catch (error) {
      console.error('Error loading supervisor assignments:', error);
    }
  };

  const loadAgencies = async () => {
    const { data } = await supabase
      .from('agencies')
      .select('id, name')
      .eq('is_approved', true)
      .order('name');
    setAgencies(data || []);
  };

  const loadStats = async () => {
    if (!user) return;

    try {
      // Get active patients count
      let activeQuery = supabase.from('patients').select('*', { count: 'exact', head: true }).eq('is_active', true);
      if (user.role === 'agency_admin' && user.agency_id) {
        activeQuery = activeQuery.eq('agency_id', user.agency_id);
      } else if (agencyFilter) {
        activeQuery = activeQuery.eq('agency_id', agencyFilter);
      }
      const { count: activeCount } = await activeQuery;

      // Get inactive patients count
      let inactiveQuery = supabase.from('patients').select('*', { count: 'exact', head: true }).eq('is_active', false);
      if (user.role === 'agency_admin' && user.agency_id) {
        inactiveQuery = inactiveQuery.eq('agency_id', user.agency_id);
      } else if (agencyFilter) {
        inactiveQuery = inactiveQuery.eq('agency_id', agencyFilter);
      }
      const { count: inactiveCount } = await inactiveQuery;

      // Get new patients this week
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      let newThisWeekQuery = supabase.from('patients').select('*', { count: 'exact', head: true }).gte('created_at', weekAgo);
      if (user.role === 'agency_admin' && user.agency_id) {
        newThisWeekQuery = newThisWeekQuery.eq('agency_id', user.agency_id);
      } else if (agencyFilter) {
        newThisWeekQuery = newThisWeekQuery.eq('agency_id', agencyFilter);
      }
      const { count: newThisWeekCount } = await newThisWeekQuery;

      // Get total sessions count
      let sessionsQuery = supabase.from('triage_sessions').select('*', { count: 'exact', head: true });
      if (user.role === 'agency_admin' && user.agency_id) {
        sessionsQuery = sessionsQuery.eq('agency_id', user.agency_id);
      } else if (agencyFilter) {
        sessionsQuery = sessionsQuery.eq('agency_id', agencyFilter);
      }
      const { count: sessionsCount } = await sessionsQuery;

      // Get unassigned patients count (patients with no active supervisor assignments)
      let unassignedCount = 0;
      if (canManageAssignments) {
        // Get all patient IDs
        let patientIdsQuery = supabase.from('patients').select('id').eq('is_active', true);
        if (user.role === 'agency_admin' && user.agency_id) {
          patientIdsQuery = patientIdsQuery.eq('agency_id', user.agency_id);
        } else if (agencyFilter) {
          patientIdsQuery = patientIdsQuery.eq('agency_id', agencyFilter);
        }
        const { data: allPatients } = await patientIdsQuery;
        
        // Get all assigned patient IDs
        const { data: assignments } = await supabase
          .from('patient_assignments')
          .select('patient_id')
          .is('revoked_at', null);
        
        const assignedPatientIds = new Set((assignments || []).map(a => a.patient_id));
        unassignedCount = (allPatients || []).filter(p => !assignedPatientIds.has(p.id)).length;
      }

      setStats({
        activeCount: activeCount || 0,
        inactiveCount: inactiveCount || 0,
        newThisWeek: newThisWeekCount || 0,
        totalSessions: sessionsCount || 0,
        unassignedCount
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPatients = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const from = (currentPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('patients')
        .select('*, agencies(name)', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      // Apply agency filter for agency admins
      if (user.role === 'agency_admin' && user.agency_id) {
        query = query.eq('agency_id', user.agency_id);
      } else if (agencyFilter) {
        query = query.eq('agency_id', agencyFilter);
      }

      // Apply status filter
      if (statusFilter === 'active') {
        query = query.eq('is_active', true);
      } else if (statusFilter === 'inactive') {
        query = query.eq('is_active', false);
      } else if (statusFilter === 'red_flags') {
        // Filter for patients with red flags in their triage sessions
        query = query.eq('is_active', true);
      }

      // Apply search filter
      if (searchTerm.trim()) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      // Apply supervisor filter - only show assigned patients
      if (supervisorFilter && assignedPatientIds.length > 0) {
        query = query.in('id', assignedPatientIds);
      } else if (supervisorFilter && assignedPatientIds.length === 0) {
        // Supervisor has no assignments, show empty list
        setPatients([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Error loading patients:', error);
        return;
      }

      setTotalCount(count || 0);

      const patientIds = (data || []).map((p: any) => p.id);

      // Get session counts, supervisor assignments, and red flag info for displayed patients
      const [sessionCounts, assignments, redFlagPatients] = await Promise.all([
        // Session counts
        Promise.all(
          (data || []).map(async (patient: any) => {
            const { count } = await supabase
              .from('triage_sessions')
              .select('*', { count: 'exact', head: true })
              .eq('patient_id', patient.id);
            return { patientId: patient.id, count: count || 0 };
          })
        ),
        // Supervisor assignments
        patientIds.length > 0
          ? supabase
              .from('patient_assignments')
              .select('patient_id, staff_user_id')
              .in('patient_id', patientIds)
              .is('revoked_at', null)
          : Promise.resolve({ data: [] }),
        // Red flag patients - get patients with any red flag sessions
        patientIds.length > 0
          ? supabase
              .from('triage_sessions')
              .select('patient_id')
              .in('patient_id', patientIds)
              .eq('red_flags', true)
          : Promise.resolve({ data: [] })
      ]);

      // Create set of patients with red flags
      const redFlagPatientIds = new Set((redFlagPatients.data || []).map(r => r.patient_id));

      // Get supervisor emails
      const supervisorIds = [...new Set((assignments.data || []).map(a => a.staff_user_id))];
      let supervisorEmails: Record<string, string> = {};
      
      if (supervisorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, email')
          .in('user_id', supervisorIds);
        
        supervisorEmails = (profiles || []).reduce((acc, p) => {
          acc[p.user_id] = p.email;
          return acc;
        }, {} as Record<string, string>);
      }

      // Group assignments by patient
      const assignmentsByPatient = (assignments.data || []).reduce((acc, a) => {
        if (!acc[a.patient_id]) acc[a.patient_id] = [];
        acc[a.patient_id].push({
          user_id: a.staff_user_id,
          email: supervisorEmails[a.staff_user_id] || 'Unknown'
        });
        return acc;
      }, {} as Record<string, { user_id: string; email: string }[]>);

      // Session counts map
      const sessionCountMap = sessionCounts.reduce((acc, s) => {
        acc[s.patientId] = s.count;
        return acc;
      }, {} as Record<string, number>);

      let patientsWithData = (data || []).map((patient: any) => ({
        ...patient,
        agency_name: patient.agencies?.name,
        _count: { sessions: sessionCountMap[patient.id] || 0 },
        assigned_supervisors: assignmentsByPatient[patient.id] || [],
        hasRedFlags: redFlagPatientIds.has(patient.id)
      }));

      // Apply unassigned filter client-side
      if (showUnassignedOnly) {
        patientsWithData = patientsWithData.filter(p => p.assigned_supervisors.length === 0);
        setTotalCount(patientsWithData.length);
      }

      // Apply red flags filter client-side
      if (statusFilter === 'red_flags') {
        patientsWithData = patientsWithData.filter(p => p.hasRedFlags);
        setTotalCount(patientsWithData.length);
      }

      setPatients(patientsWithData);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPatientInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getPatientAge = (dob: string | null) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  // Selection helpers
  const togglePatientSelection = (patientId: string) => {
    setSelectedPatients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(patientId)) {
        newSet.delete(patientId);
      } else {
        newSet.add(patientId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPatients.size === patients.length) {
      setSelectedPatients(new Set());
    } else {
      setSelectedPatients(new Set(patients.map(p => p.id)));
    }
  };

  const clearSelection = () => {
    setSelectedPatients(new Set());
  };

  const handleBulkAssignmentSuccess = () => {
    clearSelection();
    loadPatients();
  };

  const confirmUnassignSupervisor = async () => {
    if (!pendingUnassign) return;
    
    try {
      const { error } = await supabase
        .from('patient_assignments')
        .update({ revoked_at: new Date().toISOString() })
        .eq('patient_id', pendingUnassign.patientId)
        .eq('staff_user_id', pendingUnassign.supervisorUserId)
        .is('revoked_at', null);

      if (error) throw error;

      toast.success(`Unassigned ${pendingUnassign.supervisorEmail.split('@')[0]} from patient`);
      loadPatients();
    } catch (error) {
      console.error('Error unassigning supervisor:', error);
      toast.error('Failed to unassign supervisor');
    } finally {
      setPendingUnassign(null);
    }
  };

  const handleUnassignSupervisor = (
    patientId: string, 
    patientName: string,
    supervisorUserId: string, 
    supervisorEmail: string
  ) => {
    setPendingUnassign({ patientId, patientName, supervisorUserId, supervisorEmail });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-10 bg-muted rounded"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col space-y-2">
            <h1 className="text-3xl font-bold text-foreground">Patients</h1>
            <p className="text-muted-foreground">
              Manage and view patient records {user?.agency ? `for ${user.agency.name}` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddSupervisor(true)} variant="outline">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Add Supervisor
            </Button>
            <Button onClick={() => setShowAddPatient(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add Patient
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search patients by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          {isSuperAdmin && agencies.length > 0 && (
            <Select value={agencyFilter} onValueChange={setAgencyFilter}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select Agency" />
              </SelectTrigger>
              <SelectContent>
                {agencies.map((agency) => (
                  <SelectItem key={agency.id} value={agency.id}>
                    {agency.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'active' | 'inactive' | 'red_flags')}>
            <TabsList>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="inactive">Inactive</TabsTrigger>
              <TabsTrigger value="red_flags" className="text-red-600">Red Flags</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
          {canManageAssignments && (
            <Button
              variant={showUnassignedOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowUnassignedOnly(!showUnassignedOnly)}
              className="gap-1"
            >
              <UserX className="h-4 w-4" />
              Unassigned
            </Button>
          )}
        </div>

        {/* Supervisor Filter Banner */}
        {supervisorFilter && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span className="text-sm">
                  Showing patients assigned to <span className="font-medium">{supervisorEmail || 'Supervisor'}</span>
                  <span className="text-muted-foreground ml-1">({assignedPatientIds.length} patients)</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin/supervisors')}
                >
                  <ShieldCheck className="h-4 w-4 mr-1" />
                  Manage Supervisors
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchParams({})}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filter
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className={`grid grid-cols-1 gap-4 ${canManageAssignments ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <UserCheck className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.activeCount.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Active Patients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <UserX className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.inactiveCount.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Inactive Patients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.newThisWeek.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">New This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.totalSessions.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Total Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {canManageAssignments && (
            <Card 
              className={`cursor-pointer transition-colors ${showUnassignedOnly ? 'border-primary bg-primary/5' : 'hover:bg-accent/50'}`}
              onClick={() => setShowUnassignedOnly(!showUnassignedOnly)}
            >
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <ShieldCheck className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.unassignedCount.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Unassigned</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Patients List */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Patient Directory</CardTitle>
                <CardDescription>
                  Showing {patients.length} of {totalCount.toLocaleString()} patients
                  {agencyFilter && agencies.find(a => a.id === agencyFilter) && (
                    <span className="ml-1">• {agencies.find(a => a.id === agencyFilter)?.name}</span>
                  )}
                </CardDescription>
              </div>
              {/* Pagination Controls */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE) || loading}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-20 bg-muted rounded-lg animate-pulse"></div>
                ))}
              </div>
            ) : patients.length === 0 ? (
              <div className="text-center py-8">
                <UserCheck className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-medium text-foreground">No patients found</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {searchTerm ? 'Try adjusting your search terms.' : 'No patients have been registered yet.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Bulk Actions Bar */}
                {canManageAssignments && patients.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedPatients.size === patients.length && patients.length > 0}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all patients"
                      />
                      <span className="text-sm text-muted-foreground">
                        {selectedPatients.size > 0 
                          ? `${selectedPatients.size} selected` 
                          : 'Select patients'}
                      </span>
                    </div>
                    {selectedPatients.size > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => setShowBulkAssignment(true)}
                        >
                          <ShieldCheck className="h-4 w-4 mr-1" />
                          Assign
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowBulkUnassign(true)}
                        >
                          <UserX className="h-4 w-4 mr-1" />
                          Unassign
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearSelection}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {patients.map((patient) => {
                  const displayData = getDisplayData({
                    id: patient.id,
                    first_name: patient.first_name,
                    last_name: patient.last_name,
                    email: patient.email,
                    dob: patient.dob,
                  });
                  const isSelected = selectedPatients.has(patient.id);
                  
                  return (
                    <div
                      key={patient.id}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : patient.is_active === false 
                            ? 'border-muted bg-muted/30 opacity-70' 
                            : 'border-border hover:bg-accent/50'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        {canManageAssignments && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => togglePatientSelection(patient.id)}
                            aria-label={`Select ${patient.first_name} ${patient.last_name}`}
                          />
                        )}
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className={patient.is_active === false ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground"}>
                            {displayData.displayFirstName.charAt(0)}{displayData.displayLastName.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-medium text-foreground">
                              {displayData.displayName}
                            </h3>
                            {displayData.isMasked && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="outline" className="text-xs gap-1">
                                      <EyeOff className="h-3 w-3" />
                                      HIPAA
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Patient info masked for HIPAA compliance</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {patient.hasRedFlags && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="destructive" className="text-xs gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Red Flag
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Critical Condition Cases - ER or Hospitalization recommended</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {patient.is_active === false && (
                              <Badge variant="outline" className="text-xs text-muted-foreground">
                                Inactive
                              </Badge>
                            )}
                            {patient.user_id && patient.is_active !== false && !displayData.isMasked && (
                              <Badge variant="secondary" className="text-xs">
                                Registered
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-1">
                              <Mail className="h-3 w-3" />
                              <span>{displayData.displayEmail}</span>
                            </div>
                            {patient.dob && (
                              <div className="flex items-center space-x-1">
                                <Calendar className="h-3 w-3" />
                                <span>Age {getPatientAge(patient.dob)}</span>
                              </div>
                            )}
                            {isSuperAdmin && patient.agency_name && (
                              <div className="flex items-center space-x-1">
                                <Building2 className="h-3 w-3" />
                                <span>{patient.agency_name}</span>
                              </div>
                            )}
                          </div>
                          {/* Supervisor Assignments */}
                          {canManageAssignments && patient.assigned_supervisors && patient.assigned_supervisors.length > 0 && (
                            <div className="flex items-center flex-wrap gap-1 mt-1">
                              <ShieldCheck className="h-3 w-3 text-muted-foreground" />
                              {patient.assigned_supervisors.slice(0, 3).map((supervisor) => (
                                <TooltipProvider key={supervisor.user_id}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge 
                                        variant="outline" 
                                        className="text-xs bg-primary/5 hover:bg-primary/10 group/badge gap-1 pr-1"
                                      >
                                        <span 
                                          className="cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchParams({ supervisor: supervisor.user_id });
                                          }}
                                        >
                                          {supervisor.email.split('@')[0]}
                                        </span>
                                        <button
                                          type="button"
                                          className="ml-0.5 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnassignSupervisor(patient.id, `${patient.first_name} ${patient.last_name}`, supervisor.user_id, supervisor.email);
                                          }}
                                          aria-label={`Unassign ${supervisor.email}`}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Assigned to {supervisor.email}</p>
                                      <p className="text-xs text-muted-foreground">Click name to filter, X to unassign</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ))}
                              {patient.assigned_supervisors.length > 3 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="outline" className="text-xs">
                                        +{patient.assigned_supervisors.length - 3} more
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="space-y-1">
                                        {patient.assigned_supervisors.slice(3).map((s) => (
                                          <div key={s.user_id} className="flex items-center justify-between gap-2">
                                            <span>{s.email}</span>
                                            <button
                                              type="button"
                                              className="p-0.5 rounded hover:bg-destructive/20 hover:text-destructive transition-colors"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleUnassignSupervisor(patient.id, `${patient.first_name} ${patient.last_name}`, s.user_id, s.email);
                                              }}
                                              aria-label={`Unassign ${s.email}`}
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-foreground">
                          {patient._count?.sessions || 0} sessions
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Joined {formatDate(patient.created_at)}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover z-50">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate(`/admin/patients/${patient.id}`)}>
                            <UserCog className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {patient.is_active !== false && canImpersonate({ type: 'patient', patientId: patient.id }) && (
                            <DropdownMenuItem 
                              onClick={() => openImpersonationView({
                                type: 'patient',
                                patientId: patient.id,
                                patientName: `${patient.first_name} ${patient.last_name}`,
                              })}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View as Patient
                            </DropdownMenuItem>
                          )}
                          {canManageAssignments && patient.is_active !== false && (
                            <DropdownMenuItem onClick={() => setPatientToQuickAssign(patient)}>
                              <ShieldCheck className="w-4 h-4 mr-2" />
                              Assign Supervisor
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {patient.is_active !== false ? (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setPatientToDeactivate(patient)}
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              Deactivate Patient
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => {
                                // Reactivate - direct call
                                supabase
                                  .from('patients')
                                  .update({ is_active: true })
                                  .eq('id', patient.id)
                                  .then(() => loadPatients());
                              }}
                            >
                              <UserCheck className="w-4 h-4 mr-2" />
                              Reactivate Patient
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialogs */}
        <AddPatientDialog
          open={showAddPatient}
          onOpenChange={setShowAddPatient}
          onSuccess={loadPatients}
        />
        <AddSupervisorDialog
          open={showAddSupervisor}
          onOpenChange={setShowAddSupervisor}
          onSuccess={() => {}}
        />
        <PatientDeactivateDialog
          open={!!patientToDeactivate}
          onOpenChange={(open) => !open && setPatientToDeactivate(null)}
          patient={patientToDeactivate}
          onSuccess={loadPatients}
        />
        <BulkPatientAssignmentDialog
          open={showBulkAssignment}
          onOpenChange={setShowBulkAssignment}
          selectedPatientIds={Array.from(selectedPatients)}
          onSuccess={handleBulkAssignmentSuccess}
        />
        <BulkPatientUnassignDialog
          open={showBulkUnassign}
          onOpenChange={setShowBulkUnassign}
          selectedPatientIds={Array.from(selectedPatients)}
          onSuccess={handleBulkAssignmentSuccess}
        />
        <QuickAssignDialog
          open={!!patientToQuickAssign}
          onOpenChange={(open) => !open && setPatientToQuickAssign(null)}
          patient={patientToQuickAssign}
          onSuccess={loadPatients}
        />
        <AlertDialog open={!!pendingUnassign} onOpenChange={(open) => !open && setPendingUnassign(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unassign Supervisor</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to unassign <strong>{pendingUnassign?.supervisorEmail}</strong> from <strong>{pendingUnassign?.patientName}</strong>? 
                This supervisor will no longer have access to this patient's records.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmUnassignSupervisor} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Unassign
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}